"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import Icon from "@afa-shared/afa-components/dist/Icon";
import { ContentLink } from "@components/Link/ContentLink";
import { StandardLink } from "@components/Link/StandardLink";
import RichTextRenderer from "@components/RichTextRenderer";
import Grid from "@components/client/Grid";
import { getDesktopMenu, hasVisibleChildrenForAudience } from "@utils/menuHelper";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useEscape } from "hooks/useEscape";
import useMediaQueryWrapper from "hooks/useMediaQuery";
import { ISectorToggler, useSectorToggler } from "hooks/zustand/useSectorToggle";
import { MenuItem } from "types/MenuItem";
import { type SideBar as SideBarType } from "types/SideBar";

import { resolveSlugAndPath } from "..";
import { useClickOutside } from "../../../hooks/useOutsideClick";
import { navigationTracker } from "../NavigationTracker";
import desktopStyles from "./Desktop.module.css";

const SideBar = ({
  sideBar: { heading, iconName, text, link, linkText },
}: {
  sideBar: SideBarType;
}) => {
  if (!link) {
    return null;
  }

  return (
    <div className={desktopStyles.sideBar}>
      <Grid>
        {iconName && <Icon name={iconName} size="md" className={desktopStyles.sideBarIcon} />}
        <Heading variant="h2" className={desktopStyles.sideBarHeading}>
          {heading}
        </Heading>
        <div className={desktopStyles.sideBarTextWrapper}>
          <RichTextRenderer json={text} />
        </div>
        <StandardLink
          href={link}
          className={desktopStyles.sideBarLink}
          text={linkText}
          trackingData={{
            sectionTitle: undefined,
            sectionBlockIndex: undefined,
          }}
        />
      </Grid>
    </div>
  );
};

const MainNavigation = ({
  menuItems: desktopMenuItems,
}: {
  menuItems: MenuItem[];
}): React.ReactNode | any => {
  const pathname = usePathname();
  const isTablet = useMediaQueryWrapper("tablet");
  const { slug, path } = resolveSlugAndPath(pathname, isTablet);
  const selectedSector = useSectorToggler((state: ISectorToggler) => state.selectedSector);
  const [hasHydrated, setHasHydrated] = useState(false);
  const effectiveSector = hasHydrated ? selectedSector : undefined;
  const effectiveAudienceId = effectiveSector?.audienceId;

  const desktopMenu = useMemo(
    () => getDesktopMenu(desktopMenuItems, slug, path, effectiveSector, isTablet),
    [desktopMenuItems, slug, path, effectiveSector, isTablet],
  );
  const [menuItems, setMenuItems] = useState<Array<MenuItem>>(desktopMenu);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);

  const ref = useRef(null);
  const buttonRef = useRef(null);
  const navigationContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMenuItems(desktopMenu);
    setOverlayVisible(false);
  }, [desktopMenu, hasHydrated]);

  const toggleFirstLevel = (array: Array<MenuItem>): Array<MenuItem> => {
    for (const menuItem of array) {
      menuItem.isOpened = false;
    }
    return array;
  };
  const closeAllMenus = useCallback(() => {
    const array = toggleFirstLevel(menuItems);
    setMenuItems([...array]);
    setOverlayVisible(false);
  }, [menuItems]);

  useClickOutside(ref, closeAllMenus);

  useEscape(ref, () => {
    buttonRef?.current?.focus();
    closeAllMenus();
  });

  const renderSubMenu = (item: MenuItem, levelTwoParentName: string) => {
    return (
      <ul
        className={clsx(desktopStyles.list, { [desktopStyles.isOpen]: item?.isOpened })}
        data-level={item?.level + 1}
        id={item.id}
      >
        {item.children.map((child: MenuItem) =>
          getMenuItemsRecursively(
            child,
            levelTwoParentName,
            hasHydrated && child?.hideForAudiences?.includes(effectiveAudienceId),
          ),
        )}
      </ul>
    );
  };

  const toggleItemRecursively = (item: MenuItem, array: Array<MenuItem>): Array<MenuItem> => {
    for (const menuItem of array) {
      if (menuItem?.id === item?.id) {
        menuItem.isOpened = !menuItem.isOpened;
      } else {
        toggleItemRecursively(item, menuItem?.children);
      }
    }
    return array;
  };

  const openSubmenu = (item: MenuItem) => {
    const array = toggleItemRecursively(item, menuItems);
    setMenuItems([...array]);
  };

  const ToggleDropDown = (item: MenuItem) => {
    if (!item?.children?.length) return;
    if (!item?.isOpened) {
      const closeStuff = toggleFirstLevel(menuItems);
      setMenuItems([...closeStuff]);
    }
    if (item?.children?.length) setOverlayVisible(!item.isOpened);
    const array = toggleItemRecursively(item, menuItems);

    setMenuItems([...array]);
  };

  const updateOverlayDimensions = useCallback(() => {
    if (!overlayVisible) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    if (!navigationContainerRef.current) {
      return;
    }
  }, [overlayVisible]);

  useEffect(() => {
    updateOverlayDimensions();
  }, [overlayVisible, updateOverlayDimensions, menuItems]);

  useEffect(() => {
    if (typeof window === "undefined" || !overlayVisible) {
      return;
    }

    const handleResize = () => {
      updateOverlayDimensions();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [overlayVisible, updateOverlayDimensions]);

  const renderLevelTwoItem = (item: MenuItem, levelTwoParentName: string): React.ReactNode => {
    const isButtonOnly = item?.children && item?.children.length > 0;
    if (isButtonOnly) {
      return (
        <button
          className={`${desktopStyles.invisibleButton} ${
            item?.isActive ? desktopStyles.active : ""
          }`}
          type="button"
          onClick={() => ToggleDropDown(item)}
          aria-expanded={item?.isOpened}
          aria-controls={item.id}
          ref={item?.level === 2 && item?.isOpened ? buttonRef : null}
          aria-label={"Öppna undermeny till " + item?.name}
          data-level={item?.level}
          data-testid={`page-subheader-container-${item?.slug}`}
        >
          {item?.name}
        </button>
      );
    }
    return (
      <ContentLink
        key={item?.id}
        href={item?.redirectsTo ? item?.redirectsTo : (item?.url ?? "")}
        className={`${desktopStyles.navLink} ${item?.isActive ? desktopStyles.active : ""}`}
        onClick={() => navigationTracker(item?.name, levelTwoParentName, "submeny")}
        noTracking={true}
        data-level={item?.level}
        data-testid={`page-subheader-link-${item?.slug}`}
        contentText={item?.name}
      >
        {item?.name}
      </ContentLink>
    );
  };

  const renderItemWrapper = (item: MenuItem, children: any): React.ReactNode => {
    const hasActiveHiddenChildren =
      !item?.isActive &&
      item?.hasActiveChildren &&
      !hasVisibleChildrenForAudience(item, effectiveAudienceId);
    const shouldHaveWrapper = item?.level !== 3 || item?.children?.length !== 0;
    if (!shouldHaveWrapper) {
      return children;
    }
    return (
      <div
        key={item?.id}
        className={`${desktopStyles.itemWrapper} ${
          item?.isActive || hasActiveHiddenChildren ? desktopStyles.active : ""
        }`}
      >
        {children}
      </div>
    );
  };

  const renderItem = (
    item: MenuItem,
    levelTwoParentName: string,
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ): React.ReactNode => {
    const hasActiveHiddenChildren =
      !item?.isActive &&
      item?.hasActiveChildren &&
      !hasVisibleChildrenForAudience(item, effectiveAudienceId);
    return (
      <>
        {item?.level !== 3 && (
          <span
            className={`${desktopStyles.decorator} ${
              item?.isActive || hasActiveHiddenChildren ? desktopStyles.active : ""
            }`}
            data-level={item?.level}
          />
        )}
        {item?.isContainer && hasVisibleChildrenForAudience(item, effectiveAudienceId) ? (
          <button
            className={`${desktopStyles.invisibleButton} ${
              item?.hasActiveChildren ? desktopStyles.inActivePath : ""
            }`}
            type="button"
            aria-label={"Öppna undermeny till " + item?.name}
            aria-expanded={item?.isOpened}
            aria-controls={item.id}
            onClick={() => openSubmenu(item)}
          >
            {item?.name}
            {item?.children?.length && (
              <Icon name={item?.isOpened ? "remove" : "add"} className={desktopStyles.toggleIcon} />
            )}
          </button>
        ) : (
          <>
            <ContentLink
              href={item?.redirectsTo ? item?.redirectsTo : item?.url ? item.url : ""}
              className={`${desktopStyles.navLink} ${
                item?.isActive || hasActiveHiddenChildren ? desktopStyles.active : ""
              } ${item?.hasActiveChildren ? desktopStyles.inActivePath : ""} ${
                item?.children?.length === 0 ? desktopStyles.noChildren : ""
              }`}
              onClick={() => navigationTracker(item?.name, levelTwoParentName, "submeny")}
              noTracking={true}
              data-level={item?.level}
              data-testid={`page-subheader-link-${item?.slug}`}
              contentText={item?.name}
            >
              {item?.name}
            </ContentLink>
            {item?.children?.length && hasVisibleChildrenForAudience(item, effectiveAudienceId) ? (
              <button
                className={desktopStyles.toggleButton}
                aria-label={"Öppna undermeny"}
                aria-expanded={item?.isOpened}
                aria-controls={item.id}
                type="button"
                onClick={() => openSubmenu(item)}
                data-testid={`page-subheader-button-${item?.slug}`}
              >
                <Icon name={item?.isOpened ? "remove" : "add"} />
              </button>
            ) : null}
          </>
        )}
      </>
    );
  };

  const getMenuItemsRecursively = (item: MenuItem, levelTwoParentName: string, hidden: boolean) => {
    const hasActiveHiddenChildren =
      !item?.isActive &&
      item?.hasActiveChildren &&
      !hasVisibleChildrenForAudience(item, effectiveAudienceId) &&
      item?.slug !== "varumarke" &&
      item?.slug !== "intressant-vetande" &&
      item?.slug !== "ingen-missad-ersattning" &&
      item?.slug !== "tank-pa-oss";

    return (
      <li
        className={clsx(desktopStyles.listItem, {
          [desktopStyles.active]: item?.isActive || hasActiveHiddenChildren,
          [desktopStyles.hidden]: hidden,
        })}
        ref={item?.level === 2 && item?.isOpened ? ref : null}
        key={`${item?.level}_${item?.id}`}
        data-level={item?.level}
      >
        <>
          {item?.level === 2
            ? renderLevelTwoItem(item, levelTwoParentName)
            : renderItemWrapper(item, renderItem(item, levelTwoParentName))}
        </>

        {item?.children?.length ? (
          item?.level === 2 ? (
            <div
              className={`${desktopStyles.childWrapper} ${
                item?.isOpened ? desktopStyles.dropdownOpen : ""
              }`}
            >
              {renderSubMenu(item, levelTwoParentName)}
              {item?.sideBar && <SideBar sideBar={item.sideBar} />}
            </div>
          ) : (
            renderSubMenu(item, levelTwoParentName)
          )
        ) : null}
      </li>
    );
  };

  const overlayClassName = clsx(desktopStyles.overlay, {
    [desktopStyles.isVisible]: overlayVisible,
  });

  return (
    <div
      className={desktopStyles.desktopNavigation}
      data-testid={"desktop-menu"}
      ref={navigationContainerRef}
    >
      {overlayVisible && (
        <div className={overlayClassName} onClick={closeAllMenus} role="presentation" />
      )}
      <ul className={`${desktopStyles.list}`} data-level={2}>
        {menuItems?.map((item: MenuItem) => {
          const hidden = hasHydrated && item?.hideForAudiences?.includes(effectiveAudienceId);

          return getMenuItemsRecursively(item, item?.name, hidden);
        })}
      </ul>
    </div>
  );
};

export default MainNavigation;
